import { useEffect, useRef, useState } from 'react';

/**
 * @description Hook to detect scroll direction and scrolling state.
 * Returns whether the bottom nav should be hidden (user is actively scrolling down).
 * When scrolling stops, the nav reappears after a configurable delay.
 */
export const useScrollDirection = (threshold = 10, stopDelay = 150) => {
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);
  const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const diff = currentScrollY - lastScrollY.current;

      if (Math.abs(diff) > threshold) {
        // Scrolling down → hide; Scrolling up → show
        setIsHidden(true);
        lastScrollY.current = currentScrollY;
      }

      // Reset on scroll stop
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      scrollTimeout.current = setTimeout(() => {
        setIsHidden(false); // Show when scrolling stops
      }, stopDelay);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [threshold, stopDelay]);

  return isHidden;
};
